import { mergeAttributes, Node } from '@tiptap/core'
import { VueNodeViewRenderer } from '@tiptap/vue-3'
import {
  branchFromConditionElement,
  legacyConditionsFromBranches,
  migrateLegacyConditionsToBranches
} from '../liquidConditionals'
import Component from './ConditionalToken.vue'

function branchesFromElement(element) {
  const branches = []
  const conditionSpans = element.querySelectorAll('[data-condition]:not([data-condition-else])')

  conditionSpans.forEach((span) => {
    const branch = branchFromConditionElement(span)
    if (branch) {
      branches.push({
        ...branch,
        content: span.innerHTML
      })
    }
  })

  return branches
}

export default Node.create({
  name: 'liquidConditional',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      conditionalId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-conditional'),
        renderHTML: (attributes) => {
          if (!attributes.conditionalId) return {}
          return { 'data-conditional': attributes.conditionalId }
        }
      },
      branches: {
        default: [],
        parseHTML: (element) => branchesFromElement(element),
        renderHTML: () => ({})
      },
      conditions: {
        default: [],
        parseHTML: (element) => legacyConditionsFromBranches(branchesFromElement(element)),
        renderHTML: () => ({})
      },
      // Short, human-friendly preview of the fallback content
      elseContent: {
        default: '',
        parseHTML: (element) => {
          const elseSpan = element.querySelector('[data-condition-else]')
          if (elseSpan) {
            // Get text content, trimming whitespace and removing extra line breaks
            const textContent = elseSpan.textContent.trim().replace(/\s+/g, ' ')
            // Prefer a sentence boundary but avoid decimals/abbreviations (punctuation must be followed by space or end)
            const maxLen = 80
            const slice = textContent.slice(0, maxLen)
            const boundaryMatch = slice.match(/[.!?](?=\s|$)/)
            if (boundaryMatch && boundaryMatch.index !== undefined && boundaryMatch.index > 40) {
              return slice.slice(0, boundaryMatch.index + 1).trim()
            }
            return slice.trim()
          }
          return ''
        },
        renderHTML: () => ({})
      },
      // Full HTML fallback content preserved for round-tripping
      elseHtml: {
        default: '',
        parseHTML: (element) => {
          const elseSpan = element.querySelector('[data-condition-else]')
          return elseSpan ? elseSpan.innerHTML : ''
        },
        renderHTML: () => ({})
      }
    }
  },

  parseHTML() {
    return [
      { tag: 'span[data-conditional]' },
      // Allow block wrapper to safely contain paragraphs/headings/lists
      { tag: 'div[data-conditional]' }
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const branches = node.attrs.branches?.length
      ? node.attrs.branches
      : migrateLegacyConditionsToBranches(node.attrs.conditions || [])
    const { conditionalId, elseHtml } = node.attrs
    const attrs = mergeAttributes(HTMLAttributes, { 'data-conditional': conditionalId })

    const content = []
    const blockTagRegex = /<(p|h1|h2|h3|h4|h5|h6|ul|ol|li|div|blockquote|pre)\b/i
    const hasBlockContent =
      branches.some((branch) => blockTagRegex.test(String(branch.content || ''))) ||
      (!!elseHtml && blockTagRegex.test(String(elseHtml)))
    const wrapperTag = hasBlockContent ? 'div' : 'span'
    const segmentTag = hasBlockContent ? 'div' : 'span'

    branches.forEach((branch) => {
      const firstRule = branch.rules?.[0]
      const branchAttrs = {
        'data-condition': '',
        'data-condition-match': branch.match === 'any' ? 'any' : 'all',
        'data-condition-rules': JSON.stringify(branch.rules || [])
      }

      if (firstRule) {
        branchAttrs['data-condition-property'] = firstRule.token
        branchAttrs['data-condition-value'] = String(firstRule.value)
        if (firstRule.operator && firstRule.operator !== '==') {
          branchAttrs['data-condition-operator'] = firstRule.operator
        }
      }

      content.push([segmentTag, branchAttrs, branch.content])
    })

    if (elseHtml) content.push([segmentTag, { 'data-condition-else': '' }, elseHtml])

    return [wrapperTag, attrs, ...content]
  },

  addCommands() {
    return {
      setLiquidConditional:
        (attributes) =>
        ({ commands }) => {
          const branches = attributes.branches?.length
            ? attributes.branches
            : migrateLegacyConditionsToBranches(attributes.conditions || [])
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...attributes,
              branches,
              conditions: legacyConditionsFromBranches(branches)
            }
          })
        },
      updateLiquidConditional:
        (attributes) =>
        ({ tr, state }) => {
          const { doc } = state
          const liquidConditionalNodes = []

          doc.descendants((node, pos) => {
            if (node.type.name === this.name && node.attrs.conditionalId === attributes.conditionalId) {
              liquidConditionalNodes.push({ node, pos })
            }
          })

          if (liquidConditionalNodes.length === 0) return false

          const { node, pos } = liquidConditionalNodes[0]
          const branches = attributes.branches?.length
            ? attributes.branches
            : migrateLegacyConditionsToBranches(attributes.conditions || [])

          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            ...attributes,
            branches,
            conditions: legacyConditionsFromBranches(branches)
          })

          return true
        },
      replaceLiquidConditional:
        (conditionalId, content) =>
        ({ tr, state }) => {
          const { doc } = state
          const liquidConditionalNodes = []

          doc.descendants((node, pos) => {
            if (node.type.name === this.name && node.attrs.conditionalId === conditionalId) {
              liquidConditionalNodes.push({ node, pos })
            }
          })

          if (liquidConditionalNodes.length === 0) return false

          const { pos } = liquidConditionalNodes[0]
          tr.replaceWith(pos, pos + 1, state.schema.text(content))

          return true
        }
    }
  },

  addNodeView() {
    return VueNodeViewRenderer(Component)
  }
})
